import { Map, LocationCard } from '../../components';
import {
  Box,
  Button,
  Grid,
  makeStyles,
  withStyles,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import { getLocationFromAddress, getAddressFromLocation } from '../../fetch';
import { useContext, useEffect, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { ILocation } from '../../models/location';
import CircularProgress from '@mui/material/CircularProgress';
import { Locations } from '../../context';
import { MyLocation } from '@material-ui/icons';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@material-ui/core/InputAdornment';
import buildSettings from '../../buildSettings.json';

const CustomSearch = withStyles({
  root: {
    '& input': {
      color: '#26365e',
    },
    '& label': {
      top: '-23px',
      left: '-14px',
      backgroundColor: '#00a5bb',
      color: 'white',
      padding: '.45rem 1rem',
      borderRadius: '.5rem .5rem 0 0',
    },

    '& label.Mui-focused': {
      color: 'white',
    },

    '& .MuiInput-underline:after': {
      borderBottomColor: '#13c5dd',
    },

    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: '2px',
      top: '-2px',
      '& legend': {
        display: 'none',
      },
    },
    '& .MuiOutlinedInput-root': {
      borderColor: '#5AD5E8',
      borderRadius: '0 .5rem .5rem .5rem',

      '& fieldset': {
        borderColor: '#5AD5E8',
      },
      '&:hover fieldset': {
        borderColor: '#5AD5E8',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#5AD5E8',
      },
    },

    color: '#26e65d',
  },
})(TextField);

export const FindALocationMap = () => {
  // const [officeLocations, setOfficeLocations] = useState<location[]>([]);
  const [searchAddress, setSearchAddress] = useState('');
  const [nearestOffice, setNearestOffice] = useState<ILocation | null>(null);
  const [noResults, setNoResults] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchAddressDebounced, setSearchAddressDebounced] =
    useState(searchAddress);
  const [hasLocationBeenFound, setHasLocationBeenFound] = useState(false);
  const [isSearchDisabled, setisSearchDisabled] = useState(true);
  const classes = useStyles();

  useDebounce(
    () => {
      setSearchAddressDebounced(searchAddress);
    },
    300,
    [searchAddress]
  );

  const officeLocations = useContext(Locations);

  // https://stackoverflow.com/questions/4057665/google-maps-api-v3-find-nearest-markers
  const findClosestOffice = (lat1: number, lon1: number) => {
    if (officeLocations) {
      var pi = Math.PI;
      var R = 6371; //equatorial radius
      var distances = [];
      var closest = -1;

      for (let i = 0; i < officeLocations.length; i++) {
        var lat2 = officeLocations[i].lat;
        var lon2 = officeLocations[i].lng;

        var chLat = lat2 - lat1;
        var chLon = lon2 - lon1;

        var dLat = chLat * (pi / 180);
        var dLon = chLon * (pi / 180);

        var rLat1 = lat1 * (pi / 180);
        var rLat2 = lat2 * (pi / 180);

        var a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.sin(dLon / 2) *
            Math.sin(dLon / 2) *
            Math.cos(rLat1) *
            Math.cos(rLat2);
        var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        var d = R * c;

        distances[i] = d;
        if (closest === -1 || d < distances[closest]) {
          closest = i;
        }
      }
      return officeLocations[closest];
    }
    return null;
  };

  const findNearestOffice = (searchAddress: string) => {
    setSearching(true);
    getLocationFromAddress(searchAddress).then((searchResults) => {
      if (searchResults.data.status !== 'ZERO_RESULTS') {
        setNoResults('');
        const searchedAddress = searchResults.data.results[0].geometry.location;
        const nearestOffice = findClosestOffice(
          searchedAddress.lat,
          searchedAddress.lng
        );
        setNearestOffice(nearestOffice);
        setHasLocationBeenFound(true);
        setSearching(false);
      } else {
        setNoResults('No results');
        setSearching(false);
      }
    });
  };

  useEffect(() => {
    if (searchAddressDebounced) {
      findNearestOffice(searchAddressDebounced);
    } else {
      setNearestOffice(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchAddressDebounced]);

  return (
    <>
      <Grid container>
        <Grid className={classes.column} item xs={12} md={6}>
          <Map
            googleMapURL={`https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places&key=${buildSettings.REACT_APP_GOOGLE_KEY}`}
            loadingElement={<div style={{ height: `100%` }} />}
            containerElement={<div style={{ height: `900px` }} />}
            mapElement={<div style={{ height: `100%` }} />}
            officeLocations={officeLocations || []}
            searchAddress={searchAddressDebounced}
            nearestOffice={nearestOffice}
            isMapLoaded={() => setisSearchDisabled(false)}
          />
        </Grid>
        <Grid className={classes.column} item xs={12} md={6}>
          <Grid container>
            <Grid className={classes.locationFinder} item xs={12}>
              <Typography className={classes.heading} variant="h1">
                Location Finder
              </Typography>
              <Typography className={classes.text} variant="body1">
                Our staff strives to make each interaction with patients clear,
                concise and inviting. Search for a location closest to you by
                entering your address below.
              </Typography>

              <CustomSearch
                key="search"
                variant={'outlined'}
                disabled={isSearchDisabled}
                className={classes.search}
                classes={{ root: classes.searchRoot }}
                fullWidth
                id="Your-Address"
                label={<Typography>Your Address</Typography>}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon className={classes.searchIcon} />
                    </InputAdornment>
                  ),
                }}
                value={searchAddress}
                onChange={(event) => {
                  if (event.target.value === '') {
                    setNearestOffice(null);
                  }

                  setSearchAddress(event.target.value);
                }}
              />
              <Button
                disabled={isSearchDisabled}
                startIcon={<MyLocation />}
                onClick={() => {
                  setSearching(true);
                  navigator.geolocation.getCurrentPosition(
                    (data) => {
                      const lat = data.coords.latitude;
                      const lng = data.coords.longitude;
                      getAddressFromLocation(lat, lng).then((data) => {
                        setSearchAddress(
                          data.data.results[0].formatted_address
                        );
                        setSearching(false);
                      });
                    },
                    () => {
                      setSearching(false);
                      setNoResults('No results');
                    },
                    { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
                  );
                }}
              >
                Use Current Location
              </Button>
              {hasLocationBeenFound &&
                nearestOffice &&
                !searching &&
                !noResults && (
                  <LocationCard
                    location={nearestOffice}
                    hideProviders={false}
                    evenCards={false}
                  />
                )}
              {searching && (
                <Box className={classes.loading} sx={{ display: 'flex' }}>
                  <CircularProgress color="inherit" />
                </Box>
              )}
              {noResults && <div>{noResults}</div>}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  searchRoot: { borderColor: '#5AD5E8' },
  heading: {
    color: '#283b6a',
  },
  text: {
    color: '#283b6a',
  },
  loading: {
    color: theme.palette.secondary.light,
    justifyContent: 'space-around',
  },
  column: {
    padding: '1rem',
  },
  locationFinder: {
    color: theme.palette.primary.dark,
  },
  search: {
    margin: '2.75rem 0 .25rem 0',
  },
  searchIcon: {
    color: '#26365e',
    transform: 'scale(1.4) scaleX(-1)',
    fontSize: '1.5rem',
  },
}));
